import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useInView } from "@/hooks/use-in-view";
import { initialsOf } from "@/lib/people";
import { cn } from "@/lib/utils";

interface EmployeeAvatarProps {
  name: string;
  photoUrl: string | null;
  testId?: string;
  size?: "xlarge" | "large" | "small";
  /** Load the photo only once it scrolls into view, for long lists. */
  lazy?: boolean;
}

const SIZE_CLASSES = {
  xlarge: {
    root: "h-16 w-16 md:h-28 md:w-28",
    initials: "text-[24px] leading-[24px] md:text-[40px] md:leading-[40px]",
  },
  large: {
    root: "h-16 w-16",
    initials: "text-[24px] leading-[24px]",
  },
  small: {
    root: "h-8 w-8",
    initials: "text-[13px] leading-[13px]",
  },
};

/**
 * The Radix avatar rather than Subframe's: Subframe's renders its image with
 * `hidden`, so a photo never shows. This one also falls back to the initials
 * when the photo link has expired or the image fails to load.
 *
 * Keyed on whether there is a photo: once an image has loaded, Radix keeps
 * hiding the initials after the image is removed, which left a blank circle.
 * Radix preloads the image as soon as it mounts, so a lazy avatar holds the
 * image back until it is on screen.
 */
export function EmployeeAvatar({
  name,
  photoUrl,
  testId,
  size = "large",
  lazy = false,
}: EmployeeAvatarProps) {
  const { ref, inView } = useInView<HTMLSpanElement>(lazy);
  const classes = SIZE_CLASSES[size];

  return (
    <Avatar
      key={photoUrl ? "photo" : "initials"}
      ref={ref}
      className={classes.root}
      data-testid={testId}
    >
      {photoUrl && inView && (
        <AvatarImage
          src={photoUrl}
          alt={`Photo of ${name}`}
          className="object-cover"
        />
      )}
      <AvatarFallback
        className={cn(
          "bg-primary-navy-100 font-['Manrope'] font-[500] text-primary-navy-800",
          classes.initials,
        )}
      >
        {initialsOf(name)}
      </AvatarFallback>
    </Avatar>
  );
}
